import type { Reducer } from 'react'
import { useCallback, useReducer } from 'react'

import asyncAction from 'modules/core/lib/asyncAction'
import type { ApplicationError, Options, Return } from 'modules/core/lib/asyncAction'

interface ReducerState<ValueType> {
  running: boolean
  success: boolean
  error: boolean
  value: ValueType | ApplicationError | null
}

type ReducerAction<ValueType> =
  | {
      type: 'RUNNING'
    }
  | {
      type: 'RESULT'
      success: boolean
      value: ValueType | ApplicationError | null
    }

function reducer<ValueType>(state: ReducerState<ValueType>, action: ReducerAction<ValueType>) {
  switch (action.type) {
    case 'RUNNING':
      return {
        ...state,
        running: true,
      }
    case 'RESULT':
      return {
        ...state,
        running: false,
        success: action.success,
        error: !action.success,
        value: action.value,
      }
    default:
      throw new Error()
  }
}

interface UseAsyncActionType<ValueType, Args extends unknown[]> {
  value: ValueType | ApplicationError | null
  success: boolean
  error: boolean
  running: boolean
  performAction: (...args: Args) => Promise<Return<ValueType>>
}

/**
 * A custom hook that handles asynchronous actions and provides the current state and a function to perform the action.
 * @template ValueType - The type of the value returned by the asynchronous action.
 * @template Args - The types of the arguments accepted by the asynchronous action that will be returned.
 * @param {(...args: Args) => Promise<ValueType>} action - The asynchronous action to be performed.
 * @param {Options} [options] - Additional options to mofify the loading, error and success messages
 * @returns {UseAsyncActionType<ValueType, Args>} An object containing the current state and the performAction function.
 */
const useAsyncAction = <ValueType, Args extends unknown[]>(
  action: (...args: Args) => Promise<ValueType>,
  options?: Options
): UseAsyncActionType<ValueType, Args> => {
  const [state, dispatch] = useReducer<Reducer<ReducerState<ValueType>, ReducerAction<ValueType>>>(
    reducer,
    {
      running: false,
      success: false,
      error: false,
      value: null,
    }
  )

  const performAction = useCallback(
    async (...args: Args) => {
      dispatch({ type: 'RUNNING' })

      const { success, value } = await asyncAction(() => action(...args), options)
      dispatch({
        type: 'RESULT',
        success,
        value,
      })

      return {
        success,
        value,
      }
    },
    [action, options]
  )

  return {
    running: state.running,
    value: state.value,
    success: state.success,
    error: state.error,
    performAction,
  }
}

export default useAsyncAction
